import React, { useState, useRef, useCallback, useEffect } from "react";
import "./Header.css";
import { useLocation } from "../../hooks/useRouter";
import { useSelector, useDispatch } from "react-redux";
import { setSidebarCollapsed, setSelectedLanguage } from "../../store/actions";
import { useConstants } from "../../constant/constantProvider";
import { RBAC_APPS } from "../../constant/rbacConfig";
import { DEBUG_MODE_ENABLED } from "../../constant/globalConstants";
import AppsDropdown from "./AppsDropdown";
import DebugControls from "./DebugControls";
import DropdownSelector from "../DropdownSelector/DropdownSelector";

// hooks
import useOutsideClick from "../../hooks/useOutsideClick";
import { useAccessibleApps } from "../../hooks/useAccessibleApps";
import useIsMobile from "../../hooks/useIsMobile";

// event emitter
import { useEventEmitter } from "../../contexts/EventEmmiter";
import { useNewChat } from "../../hooks/useNewChat";

// assets
import UPL_GENIE_LOGO from "../../assets/images/upl-genie-logo.svg";
import GMT_LOGO from "../../assets/new-images/gmt-logo.svg";
import DEEP_INSIGHT_LOGO from "../../assets/new-images/deep-insights-transparent-color.svg";
import MY_APPS_ICON from "../../assets/new-images/my-apps-icon.svg";
import dropdownIcon from "../../assets/new-images/dropdown-icon.svg";
import renameIcon from "../../assets/new-images/rename-icon.svg";
import deleteIcon from "../../assets/new-images/delete-icon.svg";
import hamburgerIcon from "../../assets/new-images/hamburger-menu-icon.svg";
import pinIcon from "../../assets/new-images/pin-icon.svg";
import unpinIcon from "../../assets/new-images/unpin-icon.svg";
import translateIcon from "../../assets/new-images/translate-black-icon.svg";
import TALENT_PULSE_360_LOGO from "../../assets/icons/app-icons/talent-pulse-360-icon.svg";

const Header = () => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const location = useLocation();
  const { emit } = useEventEmitter();
  const { handleNewChat, activeSessionId } = useNewChat();

  const [isAppsOpen, setIsAppsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuRef = useRef(null);
  const appsRef = useRef(null);

  const selectedLanguage = useSelector((state) => state.state?.selectedLanguage || "English");

  const handleLanguageChange = (lang) => {
    dispatch(setSelectedLanguage(lang)); // persist selection in Redux
    changeLocale(lang);                  // swap all UI strings via ConstantsProvider
  };

  const isSidebarCollapsed = useSelector((state) => state.state?.sidebarCollapsed ?? true);
  const selectedThreadName = useSelector((state) => state.state?.selectedThreadName);
  const selectedAgent = useSelector((state) => state.state?.selectedAgent);
  const {
    MY_APPS, RENAME, DELETE, DEEP_INSIGHTS, PIN, UNPIN, changeLocale,
    ENGLISH, SPANISH, PORTUGUESE, FRENCH
  } = useConstants();

  const languages = [
    { label: ENGLISH || "English", value: "English" },
    { label: SPANISH || "Spanish", value: "Spanish" },
    { label: PORTUGUESE || "Portuguese", value: "Portuguese" },
    { label: FRENCH || "French", value: "French" },
  ];

  const { accessibleApps } = useAccessibleApps();



  const showThreadHeader = (
    (selectedAgent === RBAC_APPS.CHAT_GENIE && (
      location.pathname === "/chat" || location.pathname.startsWith("/chat/")
    )) ||
    (selectedAgent === RBAC_APPS.TALENT_PULSE_360 && (
      location.pathname === "/talent-pulse-360" || location.pathname.startsWith("/talent-pulse-360/")
    ))
  );

  const showTranslate = false && (
    location.pathname === "/chat" ||
    location.pathname.startsWith("/chat/") ||
    location.pathname === "/my-apps" ||
    location.pathname === "/video-tutorials"
  );

  // Sync language selection into ConstantsProvider based on current route
  useEffect(() => {
    if (showTranslate) {
      if (selectedLanguage) {
        changeLocale(selectedLanguage);
      }
    } else {
      changeLocale("English");
    }
  }, [location.pathname, selectedLanguage, showTranslate, changeLocale]);

  const isThreadActive = selectedThreadName && selectedThreadName.session_id === activeSessionId;

  const displayTitle = isThreadActive
    ? (selectedThreadName?.thread_title
      || selectedThreadName?.thread_name
      || selectedThreadName?.session_id
      || "")
    : "";

  // Close dropdowns on outside click
  useOutsideClick(menuRef, useCallback(() => setIsMenuOpen(false), []));
  useOutsideClick(appsRef, useCallback(() => setIsAppsOpen(false), []));

  const handleRenameClick = () => {
    setIsMenuOpen(false);
    emit("headerOpenRenameModal", selectedThreadName);
  };

  const handleDeleteClick = () => {
    setIsMenuOpen(false);
    emit("headerOpenDeleteModal", selectedThreadName);
  };

  const handlePinClick = () => {
    setIsMenuOpen(false);
    emit("headerOpenPinModal", selectedThreadName);
  };

  const isDeepInsights = selectedAgent === RBAC_APPS.DEEP_INSIGHTS;

  if (isDeepInsights) {
    if (!isMobile) return null;
    return (
      <header className={`header hidden-deep-insights-header ${isSidebarCollapsed ? "sidebar-collapsed" : "sidebar-expanded"}`}>
        <div className="header-left">
          <button
            className="mobile-sidebar-trigger"
            onClick={() => dispatch(setSidebarCollapsed(false))}
          >
            <img src={hamburgerIcon} alt="menu" />
          </button>
        </div>
      </header>
    );
  }

  return (
    <header className={`header ${isSidebarCollapsed ? "sidebar-collapsed" : "sidebar-expanded"}`}>
      <div className="header-left">
        <button
          className="mobile-sidebar-trigger"
          onClick={() => dispatch(setSidebarCollapsed(false))}
        >
          <img src={hamburgerIcon} alt="menu" />
        </button>

        {showThreadHeader && displayTitle ? (
          <div className="header-thread-container" ref={menuRef}>
            <button
              className="header-thread-trigger"
              onClick={() => setIsMenuOpen((prev) => !prev)}
            >
              <span className="header-thread-title" title={displayTitle}>
                {displayTitle}
              </span>
              <img
                src={dropdownIcon}
                alt="expand"
                className={`header-chevron-icon ${isMenuOpen ? "open" : ""}`}
              />
            </button>

            {isMenuOpen && (
              <div className="global-action-dropdown dropdown-fade" style={{ top: 'calc(100% + 8px)', position: 'absolute' }}>
                <button className="global-action-item" onClick={handlePinClick}>
                  <img
                    src={selectedThreadName?.pin_flag ? unpinIcon : pinIcon}
                    alt={selectedThreadName?.pin_flag ? UNPIN : PIN}
                  />
                  <span>{selectedThreadName?.pin_flag ? UNPIN : PIN}</span>
                </button>
                <button className="global-action-item" onClick={handleRenameClick}>
                  <img src={renameIcon} alt="Rename" />
                  <span>{RENAME}</span>
                </button>
                <button className="global-action-item delete" onClick={handleDeleteClick}>
                  <img src={deleteIcon} alt="Delete" />
                  <span>{DELETE}</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          !isMobile && (isSidebarCollapsed || selectedAgent === RBAC_APPS.DEEP_INSIGHTS) && (
            <div className="header-logo-container">
              <img
                src={
                  selectedAgent === RBAC_APPS.GMT
                    ? GMT_LOGO
                    : selectedAgent === RBAC_APPS.DEEP_INSIGHTS
                      ? DEEP_INSIGHT_LOGO
                      : selectedAgent === RBAC_APPS.TALENT_PULSE_360
                        ? TALENT_PULSE_360_LOGO
                        : UPL_GENIE_LOGO
                }
                alt="UPL Genie Logo"
                className="brand-logo"
                onClick={handleNewChat}
              />
              {selectedAgent === RBAC_APPS.DEEP_INSIGHTS && (
                <span className="agent-tagline">{DEEP_INSIGHTS}</span>
              )}
            </div>
          )
        )}
      </div>

      <div className="header-right">
        {DEBUG_MODE_ENABLED && <DebugControls />}
        {accessibleApps.length > 0 &&
          location.pathname !== "/my-apps" &&
          !isMobile &&
          selectedAgent !== RBAC_APPS.GMT &&
          selectedAgent !== RBAC_APPS.DEEP_INSIGHTS && (
            <div ref={appsRef} className="my-apps-wrapper">
              <button className="my-apps-button" onClick={() => setIsAppsOpen(!isAppsOpen)}>
                <img src={MY_APPS_ICON} alt={MY_APPS} className="my-apps-icon" />
                <span>{MY_APPS}</span>
              </button>
              {isAppsOpen && <AppsDropdown onClose={() => setIsAppsOpen(false)} />}
            </div>
          )}
        {showTranslate && (
          <div className="header-language-selector">
            <DropdownSelector
              items={languages}
              selected={selectedLanguage}
              onChange={handleLanguageChange}
              itemConfig={{
                getLabel: (item) => item.label,
                getValue: (item) => item.value,
              }}
              placeholder={{ name: ENGLISH || "English", image: translateIcon }}
              theme="transparent"
              type="selected"
              isIconOnly={isMobile}
            />
          </div>
        )}
      </div>

    </header>
  );
};

export default Header;
